import React, { useState, useMemo, useEffect } from 'react';
import useAxiosInstance from '../../hooks/useAxios';
import { toast } from 'react-toastify';
import FormRender from '../../components/FormRender';
import { DB_MIGRATION_FORM_CONFIG } from '../../helper';
import DiffCard from '../../components/DiffCard'; 
// import mockResponse from "./data.json";
import {ModalComp} from "../../components/Modal"
import SideBySideDiffViewer from '../../components/SidebySideViewer';

export default function DBMigration() {
  const [formData, setFormData] = useState({
    table_name: '',
    from_db: '',
    to_db: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const axiosInstance = useAxiosInstance();
  const [modalData , setModalData] = useState("");
  const [activeFilter, setActiveFilter] = useState('all');
  const [migrationResults, setMigrationResults] = useState({ errors: new Map() });
  
  const [dynamicFormSettings, setDynamicFormSettings] = useState(DB_MIGRATION_FORM_CONFIG);

  useEffect(() => {
    const dbOptions = [
      // { label: "Production", value: "prod_db" },
      { label: "Test", value: "test_db" },
      { label: "Source", value: "source_db" },
      { label: "PROD", value: "prod_db" },
    ];
    
    const filteredToDbOptions = formData.from_db 
      ? dbOptions.filter(option => option.value !== formData.from_db)
      : dbOptions;
    const filteredFromDbOptions = formData.to_db 
      ? dbOptions.filter(option => option.value !== formData.to_db)
      : dbOptions;
    
    const updatedFormSettings = DB_MIGRATION_FORM_CONFIG.map(setting => {
      if (setting.saveKey === "from_db") {
        return {
          ...setting,
          options: filteredFromDbOptions
        };
      }
      if (setting.saveKey === "to_db") {
        return {
          ...setting,
          options: filteredToDbOptions
        };
      }
      return setting;
    });
    
    setDynamicFormSettings(updatedFormSettings);
  }, [formData.from_db, formData.to_db]);

  const fetchDiffs = async () => {
    setLoading(true);
    setActiveFilter('all');
    try {
      const response = await axiosInstance.post('/accounts/sync-show-diff-db/', formData);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch diffs", error);
      toast.error(error.response?.data?.message || "Failed to fetch diffs.");
      setData(null); 
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (formData.table_name && formData.from_db && formData.to_db) {
      setMigrationResults({ errors: new Map() }); 
      setSelectedRows(new Set()); // Reset selection for a new diff context.
      fetchDiffs().then(() => {
        toast.success("Differences fetched successfully!");
      });
    } else {
      toast.error("Please fill in all fields.");
    }
  };
  
  const handleMigrate = async () => {
    if (selectedRows.size === 0) {
      toast.warn("No rows selected for migration.");
      return;
    }
    
    setMigrating(true);
    setMigrationResults({ errors: new Map() });
    const payload = { ...formData, ids: Array.from(selectedRows) };
    toast.info(`Initiating migration for ${selectedRows.size} selected items.`);

    try {
      const response = await axiosInstance.post('/accounts/sync-apply-diff-db/', payload);
      const result = response.data.result;

      const successCount = (result.synced?.length || 0) + (result.created?.length || 0) + (result.updated?.length || 0);
      const failureCount = result.errors?.length || 0;

      const errorsMap = new Map(result.errors?.map(err => [err.id, err.error]) || []);
      setMigrationResults({ errors: errorsMap });

      if (failureCount > 0) {
        toast.warn(`Migration partially complete: ${successCount} successful, ${failureCount} failed.`);
      } else {
        toast.success(`Migration successful: ${successCount} items processed.`);
      }
      
      await fetchDiffs();

    } catch (error) {
      console.error("Migration failed", error);
      const errorMsg = error.response?.data?.message || "An unexpected error occurred during migration.";
      toast.error(errorMsg);

      const errorsMap = new Map();
      selectedRows.forEach(id => {
        errorsMap.set(id, "Migration request failed.");
      });
      setMigrationResults({ errors: errorsMap });
    } finally {
      setMigrating(false);
    }
  };  

  const handleOpenDiffModal = (diffDetails) => {
    setModalData(diffDetails);
  };

  const handleCloseModal = () => {
    setModalData(null);
  };

  const headers = useMemo(() => {
    if (!data || !data.diffs || data.diffs.length === 0) return [];
    const firstRow = data.diffs[0].from_db_row || data.diffs[0].to_db_row;
    if (!firstRow) return [];
    
    const excluded = new Set(data.config.exclude_difference_in_field || []);
    const uniqueField = data.config.unique_field_to_compare;

    const allHeaders = Object.keys(firstRow).filter(h => !excluded.has(h));
    
    const uniqueFieldIndex = allHeaders.indexOf(uniqueField);
    if (uniqueFieldIndex > -1) {
      allHeaders.splice(uniqueFieldIndex, 1);
      allHeaders.unshift(uniqueField);
    }
    
    return allHeaders;
  }, [data]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allRowIds = filteredDiffs.map(d => (d.from_db_row || d.to_db_row)[data.config.unique_field_to_compare]);
      setSelectedRows(new Set(allRowIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleRowSelect = (rowId) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(rowId)) newSelection.delete(rowId);
    else newSelection.add(rowId);
    setSelectedRows(newSelection);
  };
  
  const filterOptions = [
    { key: 'all', label: 'All Differences', color: 'bg-gray-100 text-gray-800' },
    { key: 'missing_source', label: 'Missing in Source', color: 'bg-red-100 text-red-800' },
    { key: 'missing_destination', label: 'Missing in Destination', color: 'bg-red-100 text-blue-800' },
    { key: 'different', label: 'Present but Different', color: 'bg-red-100 text-yellow-800' },
  ];

  const filteredDiffs = useMemo(() => {
    if (!data || !data.diffs) return [];

    return data.diffs.filter(diff => {
      switch (activeFilter) {
        case 'missing_source':
          return !diff.from_db_row && diff.to_db_row;
        case 'missing_destination':
          return diff.from_db_row && !diff.to_db_row;
        case 'different':
          return diff.from_db_row && diff.to_db_row;
        default:
          return true;
      }
    });
  }, [data, activeFilter]);

  const hasData = data && data.diffs && data.diffs.length > 0;
  const isAllSelected = filteredDiffs.length > 0 && selectedRows.size === filteredDiffs.length;
  const hasFilteredData = filteredDiffs.length > 0;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        <div className="bg-white border border-gray-200 rounded-lg p-8 mb-6 shadow-sm">
          <h1 className="text-2xl font-semibold mb-4">Database Migration Diff</h1>
          <form onSubmit={handleFormSubmit} className="flex w-full justify-between items-end space-x-4 mb-6">
            <div className='w-full flex space-x-10'>
            <FormRender
              formData={formData}
              setFormData={setFormData}
              formSettings={dynamicFormSettings}
            />
            </div>
            <button
              type="submit"
              disabled={loading || migrating}
              className="px-6 py-2 w-40 bg-black text-white font-medium rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Fetching..." : "Fetch Diffs"}
            </button>
          </form>
        </div>

        {loading && <div className="text-center py-10">Loading...</div>}

        {!loading && !data && (
          <div className="text-center py-10 text-gray-500">
            Please enter details and fetch data to see the differences.
          </div>
        )}

        {!loading && data && !hasData && (
           <div className="text-center py-10 text-gray-500">
            No differences found. The databases are in sync.
          </div>
        )}

        {hasData && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 mb-6 shadow-sm">
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3">Filter Results:</h3>
              <div className="flex flex-wrap gap-2">
                {filterOptions.map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setActiveFilter(filter.key)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeFilter === filter.key
                        ? filter.color + ' ring-2 ring-offset-2 ring-gray-500'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {filter.label} ({activeFilter === filter.key ? filteredDiffs.length :
                      data.diffs.filter(diff => {
                        switch (filter.key) {
                          case 'missing_source': return !diff.from_db_row && diff.to_db_row;
                          case 'missing_destination': return diff.from_db_row && !diff.to_db_row;
                          case 'different': return diff.from_db_row && diff.to_db_row;
                          default: return true;
                        }
                      }).length
                    })
                  </button>
                ))}
              </div>
            </div>

            {hasFilteredData && (
              <div className="flex justify-between items-center mb-4 px-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-gray-300 text-black focus:ring-black"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={migrating}
                  />
                  <label className="font-medium">
                    Select All ({filteredDiffs.length} items)
                    {activeFilter !== 'all' && (
                      <span className="text-sm text-gray-500 ml-2">
                        - Filtered from {data.diffs.length} total differences
                      </span>
                    )}
                  </label>
                  <div className="text-sm text-gray-600 ml-4">
                    <span className="font-medium">Table:</span> {formData.table_name} |
                    <span className="font-medium ml-2">From:</span> {formData.from_db.replace('_db', '').toUpperCase()} |
                    <span className="font-medium ml-2">To:</span> {formData.to_db.replace('_db', '').toUpperCase()}
                  </div>
                </div>
                <button
                  onClick={handleMigrate}
                  disabled={selectedRows.size === 0 || migrating}
                  className="px-6 py-2  bg-black text-white font-medium rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {migrating ? "Migrating..." : `Migrate Selected (${selectedRows.size})`}
                </button>
              </div>
            )}
            {hasFilteredData ? (
              <div className="space-y-2">
                {filteredDiffs.map((diff, index) => {
                  const uniqueId = (diff.from_db_row || diff.to_db_row)[data.config.unique_field_to_compare];
                  const errorMessage = migrationResults.errors.get(uniqueId);
                  return (
                    <DiffCard
                      key={uniqueId || index}
                      diff={diff}
                      headers={headers}
                      uniqueField={data.config.unique_field_to_compare}
                      isSelected={selectedRows.has(uniqueId)}
                      onSelect={handleRowSelect}
                      onOpenModal={handleOpenDiffModal}
                      errorMessage={errorMessage}
                    />
                  );
                })}
              </div>
            ) : hasData && (
              <div className="text-center py-10 text-gray-500">
                No differences match the selected filter criteria.
              </div>
            )}
          </div>
        )}
      </div>

      <ModalComp open={!!modalData} onClose={handleCloseModal}>
        <SideBySideDiffViewer data={modalData} onClose={handleCloseModal} />
      </ModalComp>
    </div>
  );
}